import {ofType} from 'redux-observable';
import {Observable, of} from 'rxjs';
import i18next from 'i18next';
import {catchError, filter, flatMap, mergeMap} from 'rxjs/operators';
import {List} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as PromptPasswordHelpers from 'web-app/react/modals-container/prompt-password-modal/helpers';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as RoleInvitationsQueries from 'web-app/react/role-invitations/queries';

import * as Api from './api';
import * as Actions from './actions';
import ContactsModel from './model';

export const fetchChildContactObservable = relationId =>
    Api.fetchChildContact(relationId).map(relation => Actions.fetchChildContactSuccess.action(relation));

export const fetchChildContactsObservable = (childId, isLoginValidationEnabled) =>
    Api.fetchChildContacts(childId).map(relations => {
        if (isLoginValidationEnabled) {
            return Actions.fetchChildContactsWithRoleInvitations.action(childId, relations);
        } else {
            return Actions.fetchChildContactsSuccess.action({childId, relations});
        }
    });

export const fetchChildContactsWithRoleInvitationsObservable = (childId, relations) => {
    // We need to fetch the roleInvitations from GQL and map them to the relations received from the /relations endpoint
    return Observable.fromPromise(
        getApolloClient().query({
            query: RoleInvitationsQueries.CheckInvitationsByChildIdsQuery,
            variables: {
                childIds: [childId],
            },
        }),
    ).map(({data}) => {
        const allRoleInvitationsForChildId =
            data?.roles.invitations.listByChildIds.result && data.roles.invitations.listByChildIds.result.length === 1
                ? data.roles.invitations.listByChildIds.result[0].invitations
                : [];

        const newRelations = relations.map(relation => {
            const invitation = allRoleInvitationsForChildId.find(
                invitation => invitation.relationId === relation.relationId,
            );
            // If the relation is a role_invitation, we need to update the relation's roles
            // (because they are not returned from the /relations endpoint)
            return invitation
                ? relation.set(
                      'roles2',
                      List([
                          {
                              roleId: invitation.roleId,
                              title: invitation.roleTitle,
                          },
                      ]),
                  )
                : relation;
        });
        return Actions.fetchChildContactsSuccess.action({
            childId,
            relations: newRelations,
        });
    });
};

const fetchChildContactsWithRoleInvitationsEpic = action$ =>
    action$.pipe(
        ofType(Actions.fetchChildContactsWithRoleInvitations.type),
        mergeMap(({payload}) =>
            fetchChildContactsWithRoleInvitationsObservable(payload.childId, payload.relations).catch(e =>
                Observable.of(Actions.fetchChildContactsFailed.action(e)),
            ),
        ),
    );

const fetchChildContactsEpic = action$ =>
    action$.pipe(
        ofType(Actions.fetchChildContacts.type),
        mergeMap(({payload}) =>
            fetchChildContactsObservable(payload.childId, payload.isLoginValidationEnabled).catch(e =>
                Observable.of(Actions.fetchChildContactsFailed.action(e)),
            ),
        ),
    );

export const updateChildContactObservable = (data, shouldUpdateSsn, shouldUpdateAddress, ssnToken) =>
    Api.updateContactData(data, shouldUpdateSsn, shouldUpdateAddress, ssnToken).map(contact =>
        Actions.updateChildContactSuccess.action(contact),
    );

export const updateKikuRelation = (relationId, kikuRelation) =>
    Api.updateKikuRelation(relationId, kikuRelation).map(kikuRelation =>
        Actions.updateKikuRelationSuccess.action(relationId, kikuRelation),
    );

export const fetchKikuRelations = childId =>
    Api.fetchKikuRelations(childId).map(kikuRelations => Actions.fetchKikuRelationsSuccess.action(kikuRelations));

const fetchSSNObservable = (relationId, password) =>
    Api.fetchSSN(relationId, password).map(result =>
        Actions.fetchChildContactSsnSuccess.action({ssn: result.ssn, updateToken: result.updateToken, relationId}),
    );

const fetchWithPassword = PromptPasswordHelpers.makePasswordModalEpic(
    action$ =>
        action$.pipe(
            ofType(Actions.fetchChildContactSsn.type),
            filter(({payload}) => payload.requiresPassword),
        ),
    (fetchAction, modalAction) =>
        fetchSSNObservable(fetchAction.payload.relationId, modalAction.payload.password).pipe(
            catchError(error => {
                if (error.statusCode === 403 || error.errorCode === 1491287226) {
                    return of(fetchAction);
                }
                return of(Actions.fetchChildContactSsnFailed.action(fetchAction.payload.relationId));
            }),
        ),
    fetchAction => of(Actions.fetchChildContactSsnFailed.action(fetchAction.payload.relationId)),
);

const createChildContact = action$ => {
    return action$.ofType(Actions.createChildContact.type).pipe(
        mergeMap(({payload}) => {
            const roles = payload.roles ?? [];
            return fetchChildContactObservable(payload.relationId).pipe(
                flatMap(response => {
                    const newContact = ContactsModel.fromAPI(response.payload).set('roles2', List(roles));
                    return [
                        Actions.createChildContactSuccess.action(response.payload.childId, newContact),
                        GlobalEventActions.updateSuccess.action(i18next.t('childProfile.relationCreated')),
                    ];
                }),
            );
        }),
    );
};

const updateChildContactEpic = action$ =>
    action$.ofType(Actions.updateChildContact.type).mergeMap(({payload}) =>
        updateChildContactObservable(
            payload.data,
            payload.shouldUpdateSsn,
            payload.shouldUpdateAddress,
            payload.ssnToken,
        )
            .flatMap(successAction => [
                successAction,
                GlobalEventActions.updateSuccess.action(i18next.t('childProfile.relationUpdated')),
            ])
            .catch(error => Observable.of(Actions.updateChildContactFailed.action(error, payload.data.relationId))),
    );

const deleteChildContactEpic = action$ =>
    action$.ofType(Actions.deleteChildContact.type).mergeMap(({payload}) => {
        const contact = payload.contact;

        return showDeleteDialogObservable({
            title: '',
            description: i18next.t('childProfile.confirmRelationDelete', {name: contact.name.fullName}),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return Api.deactivate(contact.relationId)
                    .mapTo(
                        Actions.deleteChildContactSuccess.action({
                            childId: contact.childId,
                            relationId: contact.relationId,
                        }),
                    )
                    .catch(e => Observable.of(Actions.deleteChildContactFailed.action(e, contact.relationId)));
            } else {
                return Observable.of(Actions.deleteChildContactAborted.action({relationId: contact.relationId}));
            }
        });
    });

const fetchChildRelationSsn = action$ =>
    action$
        .ofType(Actions.fetchChildContactSsn.type)
        .filter(({payload}) => !payload.requiresPassword)
        .mergeMap(({payload}) => {
            return fetchSSNObservable(payload.relationId).catch(() =>
                Observable.of(Actions.fetchChildContactSsnFailed.action(payload.relationId)),
            );
        });

const fetchProtectedAddress = action$ =>
    action$.ofType(Actions.fetchChildContactProtectedAddress.type).mergeMap(({payload: relationId}) =>
        Api.fetchProtectedAddress(relationId)
            .map(data =>
                Actions.fetchChildContactProtectedAddressSuccess.action({
                    relationId,
                    address: data.address,
                }),
            )
            .catch(error => Observable.of(Actions.fetchChildContactProtectedAddressFailed.action(error, relationId))),
    );

const fetchEmployeeContactsEpic = action$ =>
    action$.ofType(Actions.fetchEmployeeContacts.type).mergeMap(({payload}) =>
        Api.fetchEmployeeRelations(payload.employeeId)
            .map(contacts => Actions.fetchEmployeeContactsSuccess.action(payload.employeeId, contacts))
            .catch(e => Observable.of(Actions.fetchEmployeeContactsFailed.action(e))),
    );

const updateEmployeeContactEpic = action$ =>
    action$.ofType(Actions.updateEmployeeContact.type).mergeMap(({payload}) =>
        Api.updateEmployeeRelation(payload.data)
            .map(contact => Actions.updateEmployeeContactSuccess.action(contact))
            .catch(error => Observable.of(Actions.updateEmployeeContactFailed.action(error, payload.data.relationId))),
    );

const deleteEmployeeContactEpic = action$ =>
    action$.ofType(Actions.deleteEmployeeContact.type).mergeMap(({payload}) => {
        const contact = payload.contact;
        return Api.deactivateEmployeeRelation(contact.relationId)
            .mapTo(
                Actions.deleteEmployeeContactSuccess.action({
                    employeeId: contact.employeeId,
                    relationId: contact.relationId,
                }),
            )
            .catch(e => Observable.of(Actions.deleteEmployeeContactFailed.action(e, contact.relationId)));
    });

export default combineEpics(
    fetchChildContactsEpic,
    fetchChildContactsWithRoleInvitationsEpic,
    createChildContact,
    updateChildContactEpic,
    deleteChildContactEpic,
    fetchChildRelationSsn,
    fetchProtectedAddress,
    fetchEmployeeContactsEpic,
    updateEmployeeContactEpic,
    deleteEmployeeContactEpic,
    fetchWithPassword,
);
